import React, { useState } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Table,
  Badge,
  Modal,
} from "react-bootstrap";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { reservationStatus } from "../models/models";
import { useTranslation } from "react-i18next";
import { projectData } from "../services/Constants";

const MyAppointments = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!phoneNumber || phoneNumber.trim().length !== 11) {
      setError("Please enter a valid 11-digit phone number");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Query Firestore for reservations with the provided phone number
      const reservationsRef = collection(
        db,
        projectData().RESERVATIONS_COLLECTION
      );
      const q = query(
        reservationsRef,
        where("phone", "==", phoneNumber.trim())
      );
      const querySnapshot = await getDocs(q);

      const reservationsList = [];
      querySnapshot.forEach((doc) => {
        reservationsList.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      // Sort by creation date (newest first)
      reservationsList.sort((a, b) => {
        const dateA = a.createdAt?.toDate() || new Date(0);
        const dateB = b.createdAt?.toDate() || new Date(0);
        return dateB - dateA;
      });

      setReservations(reservationsList);
      setSearched(true);
    } catch (err) {
      console.error("Error fetching reservations:", err);
      setError("Failed to fetch your appointments. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case reservationStatus.PENDING:
        return (
          <Badge bg="warning" text="dark">
            {t("Pending")}
          </Badge>
        );
      case reservationStatus.FULFILLED:
        return <Badge bg="success">{t("Fulfilled")}</Badge>;
      case reservationStatus.DENIED:
        return <Badge bg="danger">{t("Denied")}</Badge>;
      case reservationStatus.CANCELLED:
        return <Badge bg="secondary">{t("Cancelled")}</Badge>;
      default:
        return (
          <Badge bg="light" text="dark">
            {status}
          </Badge>
        );
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return date instanceof Date
      ? date.toLocaleString()
      : new Date(date).toLocaleString();
  };

  const handleViewDetails = (reservation) => {
    setSelectedReservation(reservation);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
  };

  return (
    <Container className="my-5">
      <Card>
        <Card.Header as="h3" className="text-center bg-primary text-white">
          {t("MyAppointmentsTitle")}
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit} className="mb-4">
            <Form.Group className="mb-3">
              <Form.Label>{t("EnterPhone")}</Form.Label>
              <Form.Control
                type="tel"
                placeholder={t("PhonePlaceholder")}
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                pattern="[0-9]{11}"
                maxLength="11"
                required
              />
              <Form.Text className="text-muted">
                {t("EnterPhoneHelp")}
              </Form.Text>
            </Form.Group>

            {error && <Alert variant="danger">{error}</Alert>}

            <div className="d-grid">
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    <span className="ms-2">{t("Searching")}</span>
                  </>
                ) : (
                  t("FindAppointments")
                )}
              </Button>
            </div>
          </Form>

          {searched && !loading && (
            <>
              {reservations.length === 0 ? (
                <Alert variant="info">
                  {t("NoAppointmentsFound")}. {t("VerifyPhone")}.
                </Alert>
              ) : (
                <>
                  <h5>
                    {t("MyAppointmentsTitle")} ({reservations.length})
                  </h5>
                  <Table responsive striped hover className="mt-3">
                    <thead>
                      <tr>
                        <th>{t("Date")}</th>
                        <th>{t("VisitType")}</th>
                        <th>{t("ScheduledFor")}</th>
                        <th>{t("Status")}</th>
                        <th>{t("Actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reservations.map((reservation) => (
                        <tr key={reservation.id}>
                          <td>{formatDate(reservation.createdAt?.toDate())}</td>
                          <td>
                            {reservation.visitType === "urgent" ? (
                              <Badge bg="danger">{t("Urgent")}</Badge>
                            ) : (
                              <Badge bg="info">{t("Scheduled")}</Badge>
                            )}
                          </td>
                          <td>
                            {reservation.visitType === "scheduled"
                              ? `${
                                  formatDate(
                                    new Date(reservation.appointmentDate)
                                  ).split(",")[0]
                                } at ${
                                  TIME_SLOT_LABELS[
                                    reservation.appointmentTime
                                  ] || reservation.appointmentTime
                                }`
                              : t("AsapText")}
                          </td>
                          <td>{getStatusBadge(reservation.status)}</td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleViewDetails(reservation)}
                            >
                              {t("Details")}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Reservation Details Modal */}
      <Modal show={showDetailsModal} onHide={closeDetailsModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t("AppointmentDetails")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedReservation && (
            <div>
              <h5>{t("PatientInfo")}</h5>
              <Table bordered className="mb-4">
                <tbody>
                  <tr>
                    <td width="30%">
                      <strong>{t("Name")}</strong>
                    </td>
                    <td>{selectedReservation.name}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>{t("Age")}</strong>
                    </td>
                    <td>{selectedReservation.age}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>{t("Gender")}</strong>
                    </td>
                    <td>{selectedReservation.gender}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>{t("Phone")}</strong>
                    </td>
                    <td>{selectedReservation.phone}</td>
                  </tr>
                </tbody>
              </Table>

              <h5>{t("AppointmentInfo")}</h5>
              <Table bordered className="mb-4">
                <tbody>
                  <tr>
                    <td width="30%">
                      <strong>{t("Status")}</strong>
                    </td>
                    <td>{getStatusBadge(selectedReservation.status)}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>{t("VisitType")}</strong>
                    </td>
                    <td>
                      {selectedReservation.visitType === "urgent" ? (
                        <span className="text-danger">
                          {t("Urgent")} ({t("AsapText")})
                        </span>
                      ) : (
                        <span>{t("Scheduled")}</span>
                      )}
                    </td>
                  </tr>
                  {selectedReservation.visitType === "scheduled" && (
                    <>
                      <tr>
                        <td>
                          <strong>{t("Date")}</strong>
                        </td>
                        <td>
                          {
                            formatDate(
                              new Date(selectedReservation.appointmentDate)
                            ).split(",")[0]
                          }
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>{t("Time")}</strong>
                        </td>
                        <td>
                          {t(
                            `Time${selectedReservation.appointmentTime.replace(
                              "-",
                              "To"
                            )}`
                          )}
                        </td>
                      </tr>
                    </>
                  )}
                  <tr>
                    <td>
                      <strong>{t("RequestedOn")}</strong>
                    </td>
                    <td>
                      {formatDate(selectedReservation.createdAt?.toDate())}
                    </td>
                  </tr>
                </tbody>
              </Table>

              <h5>{t("LocationInfo")}</h5>
              <Table bordered>
                <tbody>
                  <tr>
                    <td width="30%">
                      <strong>{t("LocationType")}</strong>
                    </td>
                    <td>
                      {selectedReservation.useCurrentLocation
                        ? t("GPSLocation")
                        : t("ManualAddress")}
                    </td>
                  </tr>
                  {!selectedReservation.useCurrentLocation ? (
                    <>
                      <tr>
                        <td>
                          <strong>{t("District")}</strong>
                        </td>
                        <td>{selectedReservation.district}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>{t("Address")}</strong>
                        </td>
                        <td>{selectedReservation.address}</td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td>
                        <strong>{t("GPSCoordinates")}</strong>
                      </td>
                      <td>
                        {selectedReservation.location
                          ? `${selectedReservation.location.latitude.toFixed(
                              6
                            )}, ${selectedReservation.location.longitude.toFixed(
                              6
                            )}`
                          : "Location information not available"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDetailsModal}>
            {t("Close")}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

// Time slots mapping for display purposes
const TIME_SLOT_LABELS = {
  "8-10": "8:00 AM - 10:00 AM",
  "10-12": "10:00 AM - 12:00 PM",
  "12-14": "12:00 PM - 2:00 PM",
  "14-16": "2:00 PM - 4:00 PM",
  "16-18": "4:00 PM - 6:00 PM",
  "18-20": "6:00 PM - 8:00 PM",
  "20-22": "8:00 PM - 10:00 PM",
};

export default MyAppointments;
